/**
 * Institution CTA Component
 */

import React from 'react';
import { Grid, Column, Button } from '@carbon/react';
import { Email, Phone, Calendar } from '@carbon/icons-react';
import type { ContactInformation, CTAButton } from '../../../../shared/interfaces/InstitutionalLanding';

interface InstitutionCTAProps {
  institutionName: string;
  contactInfo: ContactInformation;
  ctaButtons: CTAButton[];
}

const InstitutionCTA: React.FC<InstitutionCTAProps> = ({ institutionName, contactInfo, ctaButtons }) => {
  return (
    <section className="institution-cta">
      <Grid narrow>
        <Column lg={16}>
          <h2>Ready to Transform Education at {institutionName}?</h2>
          <p>Join thousands of educators using AI-powered personalized learning</p>
          <div className="cta-buttons">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                kind={button.style}
                size="lg"
                onClick={() => {
                  if (button.url) window.location.href = button.url;
                }}
              >
                {button.text}
              </Button>
            ))}
          </div>
          {contactInfo.contactPerson && (
            <div className="contact-info">
              <h3>Your Dedicated Contact</h3>
              <div className="contact-details">
                <p className="contact-name">{contactInfo.contactPerson.name}</p>
                <p className="contact-title">{contactInfo.contactPerson.title}</p>
                <div className="contact-methods">
                  {contactInfo.contactPerson.email && (
                    <a href={`mailto:${contactInfo.contactPerson.email}`}>
                      <Email size={16} /> {contactInfo.contactPerson.email}
                    </a>
                  )}
                  {contactInfo.contactPerson.phone && (
                    <a href={`tel:${contactInfo.contactPerson.phone}`}>
                      <Phone size={16} /> {contactInfo.contactPerson.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </Column>
      </Grid>
      <style>{`
        .institution-cta {
          padding: var(--cds-spacing-12) 0;
          background-color: var(--cds-layer-01);
          text-align: center;
        }
        .institution-cta h2 {
          font-size: var(--cds-display-01-font-size);
          font-weight: var(--cds-display-01-font-weight);
          margin-bottom: var(--cds-spacing-05);
          color: var(--cds-text-primary);
        }
        .institution-cta p {
          font-size: var(--cds-heading-03-font-size);
          color: var(--cds-text-secondary);
          margin-bottom: var(--cds-spacing-08);
        }
        .cta-buttons {
          display: flex;
          justify-content: center;
          gap: var(--cds-spacing-05);
          margin-bottom: var(--cds-spacing-10);
          flex-wrap: wrap;
        }
        .contact-info {
          background-color: var(--cds-background);
          padding: var(--cds-spacing-08);
          max-width: 31.25rem;
          margin: 0 auto;
          border: 1px solid var(--cds-border-subtle);
          border-radius: var(--cds-border-radius);
        }
        .contact-info h3 {
          font-size: var(--cds-heading-02-font-size);
          font-weight: var(--cds-heading-02-font-weight);
          margin-bottom: var(--cds-spacing-05);
        }
        .contact-name {
          font-weight: var(--cds-font-weight-semibold);
          margin-bottom: var(--cds-spacing-02);
        }
        .contact-title {
          color: var(--cds-text-secondary);
          margin-bottom: var(--cds-spacing-05);
        }
        .contact-methods {
          display: flex;
          flex-direction: column;
          gap: var(--cds-spacing-03);
        }
        .contact-methods a {
          display: inline-flex;
          align-items: center;
          gap: var(--cds-spacing-03);
          color: var(--cds-link-primary);
          text-decoration: none;
          transition: color var(--cds-duration-fast-02) var(--cds-ease-out);
        }
        .contact-methods a:hover {
          text-decoration: underline;
        }
      `}</style>
    </section>
  );
};

export default InstitutionCTA;
